import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { HttpError, request } from "../../api/http";
import { CrmHttpError } from "../../api/crm";
import { listPaymentIssueTickets } from "../../api/crmTickets";
import { useCrmSession } from "../../context/CrmSessionContext";
import { useAuth } from "../../context/AuthContext";
import { useMarket } from "../../context/MarketContext";
import Pagination from "../OnboardingCustomers/components/Pagination";
import {
  CLASSIFICATION_LABEL,
  CLASSIFICATION_STYLES,
  COLLECTION_CLASSIFICATIONS,
  CollectionClassification,
  CollectionRow,
  CollectionsResponse,
  MONTH_STATUS_LABEL,
  MONTH_STATUS_STYLES,
  MonthStatus,
  PaymentIssueTicket,
} from "../../types/collections";
import {
  CrmCross,
  crmOverrideFor,
  crossRow as crossRowPure,
  effectiveClassification,
} from "./collectionsCrossing";
import { parseCsv, serializeCsv } from "./csv";

const DEFAULT_PAGE_SIZE = 50;
const SEARCH_DEBOUNCE_MS = 300;

// Segmentos "extra" del dashboard: Canceladas, Siniestros (claim) y En trámite
// (in_progress). Para aligerar el dashboard —cuyo foco es solo la gente que ha
// pagado para marcarla en AXA— estos segmentos NO se cargan ni se muestran: no
// se piden al backend (excludeExtraSegments=1), se ocultan sus counters/badges
// y se retiran del selector de clasificación. La lógica NO se borra: basta con
// poner este flag a `true` (y quitar el query param) para rescatarla en un
// dashboard futuro.
const ENABLE_EXTRA_SEGMENTS = false;

// Clasificaciones que se muestran en counters y en el selector de filtro,
// derivadas del flag anterior. Con los segmentos extra deshabilitados se dejan
// fuera canceled / claim / in_progress.
const EXTRA_SEGMENT_CLASSIFICATIONS: readonly CollectionClassification[] = [
  "canceled",
  "claim",
  "in_progress",
];
const VISIBLE_CLASSIFICATIONS: readonly CollectionClassification[] =
  ENABLE_EXTRA_SEGMENTS
    ? COLLECTION_CLASSIFICATIONS
    : COLLECTION_CLASSIFICATIONS.filter(
        (c) => !EXTRA_SEGMENT_CLASSIFICATIONS.includes(c),
      );

// Mismo fallback de BASE_URL que api/http.ts — se replica aquí para el export
// CSV, que bypassa el helper JSON request() (igual que Impagos).
const BASE_URL =
  process.env.REACT_APP_BACKEND_URL?.replace(/\/$/, "") ??
  "http://localhost:8000";

const fmtMonthLabel = (ym: string): string => {
  const m = /^(\d{4})-(\d{2})$/.exec(ym);
  if (!m) return ym;
  const date = new Date(Number(m[1]), Number(m[2]) - 1, 1);
  const label = new Intl.DateTimeFormat("es-ES", {
    month: "short",
    year: "2-digit",
  }).format(date);
  return label.charAt(0).toUpperCase() + label.slice(1);
};

// Anchor month options: current month back 11 months, formato 'YYYY-MM'.
function buildMonthOptions(): string[] {
  const out: string[] = [];
  const now = new Date();
  for (let i = 0; i < 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    out.push(
      `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`,
    );
  }
  return out;
}

// Cabeceras del CSV que emite el backend (ListCollectionsController). Las
// localizamos por nombre en lugar de por índice fijo para no romper el cruce si
// el orden de columnas cambia en el backend.
const CSV_HEADER_LEAD_ULID = "Lead ULID";
const CSV_HEADER_CLASSIFICATION = "Classification";

// Columnas añadidas por el cruce CRM (al final, tras las columnas de meses).
const CSV_HEADER_CRM_TICKETS = "crm_tickets";
const CSV_HEADER_CLASSIFICATION_EFFECTIVE = "classification_effective";

// Aplica el cruce CRM sobre las filas ya parseadas del CSV del backend:
//  - añade `crm_tickets` (ids+pipeline separados por ';') y
//    `classification_effective` (clasificación tras el cruce) al final.
//  - la columna `Classification` original se mantiene intacta (auditoría).
// Si `ticketsByLead` es null (CRM no disponible), añade las columnas igualmente
// pero vacías: crm_tickets = "" y classification_effective = la original.
function crossExportRows(
  rows: string[][],
  ticketsByLead: Map<string, PaymentIssueTicket[]> | null,
): string[][] {
  if (rows.length === 0) return rows;

  const header = rows[0];
  const leadIdx = header.indexOf(CSV_HEADER_LEAD_ULID);
  const classIdx = header.indexOf(CSV_HEADER_CLASSIFICATION);

  const outHeader = [
    ...header,
    CSV_HEADER_CRM_TICKETS,
    CSV_HEADER_CLASSIFICATION_EFFECTIVE,
  ];
  const out: string[][] = [outHeader];

  for (let r = 1; r < rows.length; r++) {
    const cols = rows[r];
    const leadUlid = leadIdx >= 0 ? cols[leadIdx] ?? "" : "";
    const original = classIdx >= 0 ? cols[classIdx] ?? "" : "";

    let crmTickets = "";
    let effective = original;

    const tickets =
      ticketsByLead && leadUlid ? ticketsByLead.get(leadUlid) : undefined;
    if (tickets && tickets.length > 0) {
      // ids+pipeline separados por ';' (mismo detalle que el panel expandible).
      crmTickets = tickets
        .map((t) => `${t.ticketId}:${t.pipeline}`)
        .join(";");
      // La clasificación original del backend es una CollectionClassification
      // válida; si por lo que sea no lo fuera, effectiveClassification la
      // devuelve tal cual (override null) o aplica el override sobre la string.
      const override = crmOverrideFor(original, tickets);
      effective = effectiveClassification(
        original as CollectionClassification,
        override,
      );
    }

    out.push([...cols, crmTickets, effective]);
  }

  return out;
}

export default function Collections() {
  const { token } = useAuth();
  const { markets } = useMarket();
  const { connected } = useCrmSession();

  const [data, setData] = useState<CollectionsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Tickets de impago/disputa del CRM, indexados por leadUlid canónico.
  const [crmByLead, setCrmByLead] = useState<
    Map<string, PaymentIssueTicket[]>
  >(new Map());
  const [crmError, setCrmError] = useState<string | null>(null);

  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [medor, setMedor] = useState("");
  const [classification, setClassification] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("");
  const [leadStatus, setLeadStatus] = useState("");
  const monthOptions = useMemo(buildMonthOptions, []);
  const [month, setMonth] = useState(monthOptions[0]);
  const [onlyUnpaidAtCut, setOnlyUnpaidAtCut] = useState(false);

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  const [exporting, setExporting] = useState(false);

  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [search]);

  // Query string de filtros, compartida por la lista paginada y el export CSV
  // para que ambos endpoints reflejen siempre los mismos filtros activos.
  const buildFilterParams = useCallback(() => {
    const params = new URLSearchParams();
    if (debouncedSearch) params.set("search", debouncedSearch);
    if (medor) params.set("medor", medor);
    if (classification) params.set("classification", classification);
    if (paymentMethod) params.set("paymentMethod", paymentMethod);
    if (leadStatus) params.set("leadStatus", leadStatus);
    if (month) params.set("month", month);
    if (onlyUnpaidAtCut) params.set("unpaidAtCut", "1");
    // Pide al backend que excluya canceladas / siniestros / en trámite del
    // universo (lista, counts y CSV) mientras el dashboard esté enfocado en
    // pólizas pagadas. Reversible: si ENABLE_EXTRA_SEGMENTS pasa a true no se
    // envía el param y el contrato vuelve al comportamiento completo.
    if (!ENABLE_EXTRA_SEGMENTS) params.set("excludeExtraSegments", "1");
    return params;
  }, [
    debouncedSearch,
    medor,
    classification,
    paymentMethod,
    leadStatus,
    month,
    onlyUnpaidAtCut,
  ]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const params = buildFilterParams();
    params.set("page", String(page + 1));
    params.set("pageSize", String(pageSize));
    try {
      const response = await request<CollectionsResponse>(
        `/api/admin/operations/collections?${params.toString()}`,
        { token, markets },
      );
      setData(response);
    } catch (err) {
      setError(
        err instanceof HttpError ? `HTTP ${err.status}` : "No se pudo cargar",
      );
    } finally {
      setLoading(false);
    }
  }, [token, markets, buildFilterParams, page, pageSize]);

  useEffect(() => {
    load();
  }, [load]);

  // Cruce CRM: cuando cambian los meses de la respuesta, pedimos los tickets de
  // impago/disputa de esa ventana y los indexamos por lead. El CRM es opcional:
  // si no hay sesión o falla, la vista sigue funcionando sin el cruce.
  useEffect(() => {
    const months = data?.months ?? [];
    if (!connected || months.length === 0) {
      setCrmByLead(new Map());
      setCrmError(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await listPaymentIssueTickets(months);
        if (cancelled) return;
        const map = new Map<string, PaymentIssueTicket[]>();
        for (const t of res.items) {
          const list = map.get(t.leadId) ?? [];
          list.push(t);
          map.set(t.leadId, list);
        }
        setCrmByLead(map);
        setCrmError(null);
      } catch (err) {
        if (cancelled) return;
        setCrmByLead(new Map());
        setCrmError(
          err instanceof CrmHttpError
            ? `No se pudo cruzar con el CRM (HTTP ${err.status})`
            : "No se pudo cruzar con el CRM",
        );
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [data?.months, connected]);

  const clearFilters = () => {
    setSearch("");
    setMedor("");
    setClassification("");
    setPaymentMethod("");
    setLeadStatus("");
    setOnlyUnpaidAtCut(false);
    setMonth(monthOptions[0]);
    setPage(0);
  };

  const handleExportCsv = async () => {
    if (exporting) return;

    // El export cruza el CSV del backend con los tickets del CRM en cliente. Si
    // el CRM no está disponible (sin sesión o el cruce de pantalla falló), el
    // export sigue adelante pero SIN cruce: se avisa al usuario para que decida.
    const crmAvailable = connected && !crmError;
    if (!crmAvailable) {
      const proceed = window.confirm(
        "El cruce con los tickets del CRM no está disponible ahora mismo. " +
          "El CSV se exportará sin la reclasificación del CRM: las columnas " +
          "crm_tickets y classification_effective irán vacías / con la " +
          "clasificación original.\n\n¿Exportar igualmente?",
      );
      if (!proceed) return;
    }

    setExporting(true);
    setError(null);
    try {
      const params = buildFilterParams();
      params.set("format", "csv");
      const headers: Record<string, string> = {};
      if (token) headers["Authorization"] = `Bearer ${token}`;
      if (markets && markets.length > 0) headers["X-Market"] = markets.join(",");
      const response = await fetch(
        `${BASE_URL}/api/admin/operations/collections?${params.toString()}`,
        { headers },
      );
      if (!response.ok) {
        throw new HttpError(response.status, await response.text());
      }
      // Parseamos el CSV del backend (ojo BOM UTF-8 + fputcsv defaults) para
      // aplicar en cliente el MISMO override CRM que la pantalla y añadir dos
      // columnas al final. La columna original `Classification` se conserva
      // intacta para auditoría.
      const rawText = await response.text();
      const rows = parseCsv(rawText);
      const crossed = crossExportRows(rows, crmAvailable ? crmByLead : null);
      const outText = serializeCsv(crossed);

      const blob = new Blob([outText], { type: "text/csv;charset=utf-8" });
      const url = window.URL.createObjectURL(blob);
      const stamp = new Date()
        .toISOString()
        .slice(0, 16)
        .replace(/[-T:]/g, "")
        .replace(/(\d{8})(\d{4})/, "$1-$2");
      const a = document.createElement("a");
      a.href = url;
      a.download = `collections-${stamp}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(
        err instanceof HttpError
          ? `HTTP ${err.status} al exportar`
          : "No se pudo exportar",
      );
    } finally {
      setExporting(false);
    }
  };

  const toggleExpanded = (id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // Cruce CRM en cliente para una fila (tabla en pantalla). La lógica pura vive
  // en collectionsCrossing.ts para compartirse con el export CSV.
  const crossRow = useCallback(
    (row: CollectionRow): CrmCross | null => crossRowPure(row, crmByLead),
    [crmByLead],
  );

  const anchorMonths = data?.months ?? [];

  // Contadores por clasificación. Preferimos `counts` del backend (global sobre
  // todo el universo filtrado, no solo la página). Si el backend aún no lo envía
  // (contrato antiguo), caemos al recuento de la página visible aplicando el
  // override del CRM. El cruce CRM solo puede aplicarse sobre las filas cargadas,
  // por lo que con `counts` del servidor no se refleja la reclasificación CRM.
  const classCounts = useMemo(() => {
    if (data?.counts) {
      return {
        perfect: data.counts.perfect,
        doubtful: data.counts.doubtful,
        disputed: data.counts.disputed,
        unpaid: data.counts.unpaid,
        in_progress: data.counts.inProgress,
        canceled: data.counts.canceled,
        claim: data.counts.claim,
      } as Record<CollectionClassification, number>;
    }
    const counts: Record<CollectionClassification, number> = {
      perfect: 0,
      doubtful: 0,
      disputed: 0,
      unpaid: 0,
      in_progress: 0,
      canceled: 0,
      claim: 0,
    };
    for (const row of data?.items ?? []) {
      const cross = crossRow(row);
      const effective = effectiveClassification(
        row.classification,
        cross?.override ?? null,
      );
      counts[effective]++;
    }
    return counts;
  }, [data, crossRow]);

  if (loading && !data) {
    return (
      <div style={{ padding: "1.5rem", color: "#73879C" }}>
        Cargando cobranza…
      </div>
    );
  }

  const monthColSpan = anchorMonths.length || 4;

  return (
    <div style={{ padding: "1rem 1.5rem", maxWidth: 1700, margin: "0 auto" }}>
      <div
        style={{
          marginBottom: "0.75rem",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: "1rem",
        }}
      >
        <div>
          <h2 style={{ margin: 0, fontSize: "1.15rem", color: "#2A3F54" }}>
            Cobranza
          </h2>
          <p
            style={{
              margin: "0.25rem 0 0",
              color: "#73879C",
              fontSize: ".85rem",
            }}
          >
            Estado de cobro por póliza en una ventana de 4 meses, cruzado con los
            tickets de impago/disputa del CRM.
          </p>
        </div>
        <button
          type="button"
          onClick={handleExportCsv}
          disabled={exporting || loading}
          title="Descargar un CSV con las pólizas que coinciden con los filtros activos"
          style={{
            padding: "0.45rem 0.85rem",
            borderRadius: "0.4rem",
            background: exporting ? "#73879C" : "#0069d9",
            color: "white",
            border: `1px solid ${exporting ? "#73879C" : "#0069d9"}`,
            fontSize: ".82rem",
            fontWeight: 600,
            cursor: exporting || loading ? "not-allowed" : "pointer",
            whiteSpace: "nowrap",
          }}
        >
          {exporting ? "Exportando…" : "Exportar CSV"}
        </button>
      </div>

      {/* Contadores por clasificación (globales del backend; si no vienen,
          recuento de la página actual con override CRM) */}
      {data && (
        <div
          style={{
            display: "flex",
            gap: "0.5rem",
            flexWrap: "wrap",
            marginBottom: "0.65rem",
          }}
        >
          <Counter label="Total" value={data.total} />
          {VISIBLE_CLASSIFICATIONS.map((c) => (
            <Counter
              key={c}
              label={CLASSIFICATION_LABEL[c]}
              value={classCounts[c]}
              styles={CLASSIFICATION_STYLES[c]}
            />
          ))}
        </div>
      )}

      <FiltersBar
        search={search}
        onSearch={(v) => {
          setSearch(v);
          setPage(0);
        }}
        medor={medor}
        onMedor={(v) => {
          setMedor(v);
          setPage(0);
        }}
        classification={classification}
        onClassification={(v) => {
          setClassification(v);
          setPage(0);
        }}
        paymentMethod={paymentMethod}
        onPaymentMethod={(v) => {
          setPaymentMethod(v);
          setPage(0);
        }}
        leadStatus={leadStatus}
        onLeadStatus={(v) => {
          setLeadStatus(v);
          setPage(0);
        }}
        month={month}
        monthOptions={monthOptions}
        onMonth={(v) => {
          setMonth(v);
          setPage(0);
        }}
        onlyUnpaidAtCut={onlyUnpaidAtCut}
        onOnlyUnpaidAtCut={(v) => {
          setOnlyUnpaidAtCut(v);
          setPage(0);
        }}
        onClear={clearFilters}
      />

      {crmError && (
        <div
          style={{
            padding: "0.5rem 0.85rem",
            background: "#fff3cd",
            color: "#856404",
            borderRadius: "0.45rem",
            marginBottom: "0.55rem",
            fontSize: ".8rem",
          }}
        >
          {crmError}. Las filas se muestran sin el cruce de tickets.
        </div>
      )}

      {error && (
        <div
          style={{
            padding: "0.6rem 0.85rem",
            background: "#fde2e2",
            color: "#b32424",
            borderRadius: "0.45rem",
            marginBottom: "0.65rem",
            fontSize: ".82rem",
          }}
        >
          {error}
        </div>
      )}

      <div
        style={{
          background: "white",
          borderRadius: "0.6rem",
          boxShadow: "0 1px 3px rgba(0,0,0,.04)",
          overflow: "hidden",
          opacity: loading ? 0.6 : 1,
          transition: "opacity .15s ease",
        }}
      >
        <div style={{ overflowX: "auto" }}>
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              fontSize: ".85rem",
            }}
          >
            <thead>
              <tr style={{ background: "#f8f9fb" }}>
                <Th>Póliza</Th>
                <Th>Nombre</Th>
                <Th>Medor</Th>
                <Th>Pago</Th>
                <Th>Estado lead</Th>
                {anchorMonths.map((m) => (
                  <Th key={m} style={{ textAlign: "center" }}>
                    {fmtMonthLabel(m)}
                  </Th>
                ))}
                <Th>Clasificación</Th>
                <Th>Corte (día 15)</Th>
              </tr>
            </thead>
            <tbody>
              {data === null || data.items.length === 0 ? (
                <tr>
                  <td
                    colSpan={5 + monthColSpan + 2}
                    style={{
                      padding: "1rem",
                      color: "#73879C",
                      textAlign: "center",
                    }}
                  >
                    Sin pólizas para los filtros aplicados
                  </td>
                </tr>
              ) : (
                data.items.map((row) => (
                  <Row
                    key={row.leadUlid}
                    row={row}
                    anchorMonths={anchorMonths}
                    cross={crossRow(row)}
                    expanded={expanded.has(row.leadUlid)}
                    onToggleExpanded={() => toggleExpanded(row.leadUlid)}
                  />
                ))
              )}
            </tbody>
          </table>
        </div>

        {data && data.total > 0 && (
          <Pagination
            page={page}
            pageSize={pageSize}
            total={data.total}
            onPageChange={(p) => setPage(p)}
            onPageSizeChange={(s) => {
              setPageSize(s);
              setPage(0);
            }}
            disabled={loading}
          />
        )}
      </div>
    </div>
  );
}

function Row({
  row,
  anchorMonths,
  cross,
  expanded,
  onToggleExpanded,
}: {
  row: CollectionRow;
  anchorMonths: string[];
  cross: CrmCross | null;
  expanded: boolean;
  onToggleExpanded: () => void;
}) {
  // Clasificación efectiva: el override del CRM aplica salvo que Stripe ya la
  // marque como impagada (ese estado manda sobre "dudosa").
  const effectiveClass: CollectionClassification = effectiveClassification(
    row.classification,
    cross?.override ?? null,
  );
  const reclassified = effectiveClass !== row.classification;
  const classStyle = CLASSIFICATION_STYLES[effectiveClass];

  const monthByKey = new Map(row.months.map((m) => [m.month, m]));

  return (
    <>
      <tr
        style={{
          borderBottom: expanded ? "none" : "1px solid #f1f3f5",
          background: cross?.conflict ? "#fffbea" : "white",
        }}
      >
        <Td mono>
          <Link
            to={`/leads/${row.leadUlid}`}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              color: "#0069d9",
              textDecoration: "none",
              fontWeight: 600,
            }}
          >
            {row.policyNumber || "—"}
          </Link>
          {row.subscriptionStatus && (
            <div style={{ fontSize: ".68rem", color: "#73879C" }}>
              {row.subscriptionStatus}
              {row.frequency ? ` · ${row.frequency}` : ""}
            </div>
          )}
        </Td>
        <Td>
          <div>{row.fullName || "—"}</div>
          <div style={{ fontSize: ".7rem", color: "#73879C" }}>
            {row.market ?? "—"}
            {row.brokerName ? ` · ${row.brokerName}` : ""}
          </div>
        </Td>
        <Td>
          {row.medor ? (
            <span
              title={
                row.medorSource === "exception"
                  ? "Medor asignado por excepción manual (no por regla)"
                  : undefined
              }
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "0.25rem",
                padding: "0.1rem 0.45rem",
                borderRadius: "0.3rem",
                fontSize: ".72rem",
                fontWeight: 600,
                background:
                  row.medorSource === "exception" ? "#e6dcf5" : "#e2e8f0",
                color: row.medorSource === "exception" ? "#5b3a9c" : "#334155",
                cursor: row.medorSource === "exception" ? "help" : "default",
              }}
            >
              {row.medor}
              {row.medorSource === "exception" && (
                <span style={{ fontSize: ".7rem" }}>*</span>
              )}
            </span>
          ) : (
            "—"
          )}
        </Td>
        <Td>{row.paymentMethod ?? "—"}</Td>
        <Td>{row.leadStatus}</Td>
        {anchorMonths.map((m) => {
          const cell = monthByKey.get(m);
          const status: MonthStatus = cell?.status ?? "NONE";
          const style = MONTH_STATUS_STYLES[status];
          return (
            <Td key={m} style={{ textAlign: "center" }}>
              {status === "NONE" ? (
                <span style={{ color: "#adb5bd" }}>—</span>
              ) : (
                <span
                  title={
                    cell?.paymentDate
                      ? `${MONTH_STATUS_LABEL[status]} · ${cell.paymentDate}`
                      : MONTH_STATUS_LABEL[status]
                  }
                  style={{
                    display: "inline-block",
                    minWidth: "4.5rem",
                    padding: "0.15rem 0.5rem",
                    borderRadius: "999px",
                    background: style.bg,
                    color: style.fg,
                    fontSize: ".68rem",
                    fontWeight: 700,
                    letterSpacing: ".3px",
                    whiteSpace: "nowrap",
                  }}
                >
                  {MONTH_STATUS_LABEL[status]}
                </span>
              )}
            </Td>
          );
        })}
        <Td>
          <div
            style={{ display: "flex", alignItems: "center", gap: "0.35rem" }}
          >
            <span
              style={{
                display: "inline-block",
                padding: "0.15rem 0.5rem",
                borderRadius: "999px",
                background: classStyle.bg,
                color: classStyle.fg,
                fontSize: ".7rem",
                fontWeight: 700,
                whiteSpace: "nowrap",
              }}
            >
              {CLASSIFICATION_LABEL[effectiveClass]}
            </span>
            {cross && cross.tickets.length > 0 && (
              <button
                type="button"
                onClick={onToggleExpanded}
                title="Ver tickets del CRM asociados"
                style={{
                  border: "1px solid #dee2e6",
                  background: cross.conflict ? "#ffe08a" : "white",
                  borderRadius: "999px",
                  padding: "0 0.4rem",
                  fontSize: ".68rem",
                  fontWeight: 700,
                  color: "#856404",
                  cursor: "pointer",
                  lineHeight: 1.6,
                }}
              >
                {cross.conflict ? "⚠ " : ""}
                {cross.tickets.length} ticket
                {cross.tickets.length > 1 ? "s" : ""}
              </button>
            )}
            {reclassified && (
              <span
                title="Reclasificada por el cruce con tickets del CRM"
                style={{ fontSize: ".7rem", color: "#856404" }}
              >
                ↺
              </span>
            )}
          </div>
        </Td>
        <Td>
          {row.unpaidAtCut ? (
            <span
              style={{
                display: "inline-block",
                padding: "0.15rem 0.5rem",
                borderRadius: "0.3rem",
                background: "#f8d7da",
                color: "#b32424",
                fontSize: ".7rem",
                fontWeight: 700,
                whiteSpace: "nowrap",
              }}
            >
              No pagado al corte
            </span>
          ) : (
            <span style={{ color: "#adb5bd" }}>—</span>
          )}
        </Td>
      </tr>
      {expanded && cross && cross.tickets.length > 0 && (
        <tr style={{ borderBottom: "1px solid #f1f3f5", background: "#fffbea" }}>
          <td colSpan={5 + (anchorMonths.length || 4) + 2} style={{ padding: 0 }}>
            <div style={{ padding: "0.6rem 0.9rem" }}>
              {cross.conflict && (
                <div
                  style={{
                    marginBottom: "0.5rem",
                    color: "#b32424",
                    fontSize: ".8rem",
                    fontWeight: 600,
                  }}
                >
                  ⚠ Conflicto entre el estado de Stripe y los tickets del CRM —
                  revisar cobro.
                </div>
              )}
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "0.35rem",
                }}
              >
                {cross.tickets.map((t) => (
                  <div
                    key={t.ticketId}
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: "0.5rem",
                      alignItems: "center",
                      fontSize: ".78rem",
                    }}
                  >
                    <Link
                      to={`/operations/tickets/${t.ticketId}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        color: "#0069d9",
                        textDecoration: "none",
                        fontWeight: 600,
                        fontFamily: "monospace",
                      }}
                    >
                      #{t.ticketId}
                    </Link>
                    <span
                      style={{
                        padding: "0.05rem 0.4rem",
                        borderRadius: "0.3rem",
                        background: "#e2e8f0",
                        color: "#334155",
                        fontSize: ".7rem",
                        fontWeight: 600,
                      }}
                    >
                      {t.pipeline}
                    </span>
                    {t.typification && (
                      <span style={{ color: "#52606d" }}>
                        {t.incidentType ? `${t.incidentType} · ` : ""}
                        {t.typification}
                      </span>
                    )}
                    {t.state && (
                      <span style={{ color: "#73879C" }}>({t.state})</span>
                    )}
                    {t.month && (
                      <span style={{ color: "#73879C" }}>· {t.month}</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

function FiltersBar({
  search,
  onSearch,
  medor,
  onMedor,
  classification,
  onClassification,
  paymentMethod,
  onPaymentMethod,
  leadStatus,
  onLeadStatus,
  month,
  monthOptions,
  onMonth,
  onlyUnpaidAtCut,
  onOnlyUnpaidAtCut,
  onClear,
}: {
  search: string;
  onSearch: (v: string) => void;
  medor: string;
  onMedor: (v: string) => void;
  classification: string;
  onClassification: (v: string) => void;
  paymentMethod: string;
  onPaymentMethod: (v: string) => void;
  leadStatus: string;
  onLeadStatus: (v: string) => void;
  month: string;
  monthOptions: string[];
  onMonth: (v: string) => void;
  onlyUnpaidAtCut: boolean;
  onOnlyUnpaidAtCut: (v: boolean) => void;
  onClear: () => void;
}) {
  return (
    <div
      style={{
        background: "white",
        borderRadius: "0.6rem",
        boxShadow: "0 1px 3px rgba(0,0,0,.04)",
        padding: "0.7rem 0.9rem",
        marginBottom: "0.65rem",
        display: "flex",
        flexWrap: "wrap",
        gap: "0.75rem",
        alignItems: "flex-end",
      }}
    >
      <Field label="Buscar (póliza / nombre)">
        <input
          type="text"
          value={search}
          onChange={(e) => onSearch(e.target.value)}
          placeholder="Póliza o nombre…"
          style={inputStyle}
        />
      </Field>
      <Field label="Medor">
        <input
          type="text"
          value={medor}
          onChange={(e) => onMedor(e.target.value)}
          placeholder="Medor…"
          style={{ ...inputStyle, width: "8rem" }}
        />
      </Field>
      <Field label="Clasificación">
        <select
          value={classification}
          onChange={(e) => onClassification(e.target.value)}
          style={inputStyle}
        >
          <option value="">Todas</option>
          {VISIBLE_CLASSIFICATIONS.map((c) => (
            <option key={c} value={c}>
              {CLASSIFICATION_LABEL[c]}
            </option>
          ))}
        </select>
      </Field>
      <Field label="Método de pago">
        <input
          type="text"
          value={paymentMethod}
          onChange={(e) => onPaymentMethod(e.target.value)}
          placeholder="Método…"
          style={{ ...inputStyle, width: "9rem" }}
        />
      </Field>
      <Field label="Estado lead">
        <input
          type="text"
          value={leadStatus}
          onChange={(e) => onLeadStatus(e.target.value)}
          placeholder="Estado…"
          style={{ ...inputStyle, width: "9rem" }}
        />
      </Field>
      <Field label="Mes ancla">
        <select
          value={month}
          onChange={(e) => onMonth(e.target.value)}
          style={inputStyle}
        >
          {monthOptions.map((m) => (
            <option key={m} value={m}>
              {fmtMonthLabel(m)}
            </option>
          ))}
        </select>
      </Field>
      <label
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: "0.4rem",
          fontSize: ".82rem",
          color: "#1f2933",
          paddingBottom: "0.35rem",
        }}
      >
        <input
          type="checkbox"
          checked={onlyUnpaidAtCut}
          onChange={(e) => onOnlyUnpaidAtCut(e.target.checked)}
        />
        Solo no pagadas al corte (día 15)
      </label>
      <button
        type="button"
        onClick={onClear}
        style={{
          padding: "0.4rem 0.75rem",
          borderRadius: "0.4rem",
          background: "white",
          color: "#73879C",
          border: "1px solid #dee2e6",
          fontSize: ".8rem",
          fontWeight: 600,
          cursor: "pointer",
        }}
      >
        Limpiar
      </button>
    </div>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: "0.2rem" }}>
      <span style={{ fontSize: ".72rem", color: "#73879C", fontWeight: 600 }}>
        {label}
      </span>
      {children}
    </label>
  );
}

function Counter({
  label,
  value,
  styles,
}: {
  label: string;
  value: number;
  styles?: { bg: string; fg: string };
}) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: "0.4rem",
        padding: "0.35rem 0.7rem",
        borderRadius: "0.5rem",
        background: styles?.bg ?? "#f1f3f5",
        color: styles?.fg ?? "#1f2933",
        fontSize: ".78rem",
        fontWeight: 600,
      }}
    >
      <span>{label}</span>
      <strong style={{ fontVariantNumeric: "tabular-nums" }}>{value}</strong>
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  padding: "0.4rem 0.55rem",
  borderRadius: "0.4rem",
  border: "1px solid #dee2e6",
  background: "#f8f9fb",
  fontSize: ".85rem",
};

function Th({
  children,
  style,
}: {
  children?: React.ReactNode;
  style?: React.CSSProperties;
}) {
  return (
    <th
      style={{
        padding: "0.55rem 0.75rem",
        textAlign: "left",
        fontSize: ".7rem",
        fontWeight: 700,
        textTransform: "uppercase",
        letterSpacing: ".4px",
        color: "#73879C",
        borderBottom: "1px solid #e9ecef",
        ...style,
      }}
    >
      {children}
    </th>
  );
}

function Td({
  children,
  mono,
  style,
}: {
  children: React.ReactNode;
  mono?: boolean;
  style?: React.CSSProperties;
}) {
  return (
    <td
      style={{
        padding: "0.55rem 0.75rem",
        fontFamily: mono ? "monospace" : "inherit",
        color: "#1f2933",
        verticalAlign: "middle",
        ...style,
      }}
    >
      {children}
    </td>
  );
}
